import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import FilterPanel from './components/FilterPanel';
import { regions, years } from '@/mocks/categories';
import { getCategoryByKey, getMoviesByType, filterMovies, sortMovies, type SortKey } from '@/utils/movie';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 20;

export default function CategoryPage() {
  const { type, subtype } = useParams<{ type: string; subtype?: string }>();
  const category = getCategoryByKey(type || '');

  const [regionsSelected, setRegionsSelected] = useState<string[]>([]);
  const [yearsSelected, setYearsSelected] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('year_desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    // Initialize filters from the optional subtype route segment
    if (subtype && regions.some((r) => r.key === subtype)) {
      setRegionsSelected([subtype]);
      setYearsSelected([]);
    } else if (subtype && years.some((y) => y.key === subtype)) {
      setYearsSelected([subtype]);
      setRegionsSelected([]);
    } else {
      setRegionsSelected([]);
      setYearsSelected([]);
    }
    setSort('year_desc');
    setPage(1);
  }, [type, subtype]);

  const source = useMemo(() => getMoviesByType(type || ''), [type]);

  const filtered = useMemo(() => {
    const f = filterMovies(source, { regions: regionsSelected, years: yearsSelected });
    return sortMovies(f, sort);
  }, [source, regionsSelected, yearsSelected, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (list: string[], setList: (v: string[]) => void, key: string) => {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
    setPage(1);
  };

  const reset = () => {
    setRegionsSelected([]);
    setYearsSelected([]);
    setSort('year_desc');
    setPage(1);
  };

  const activeCount = regionsSelected.length + yearsSelected.length;

  usePageMeta({
    title: category
      ? `${category.name}电影 - 高清电影分类 - 高清电影`
      : '电影分类 - 高清电影',
    description: category
      ? `${category.desc} 高清电影为你精选${category.name}类高清影片，提供最新${category.name}电影资源与推荐，分类清晰，查找便捷。`
      : '高清电影为你提供丰富的电影分类浏览，覆盖多种类型高清影片。',
    keywords: category
      ? `${category.name}电影,${category.name}电影推荐,高清${category.name}影片,高清电影`
      : '高清电影,电影分类,高清影片',
  });

  if (!category) {
    return (
      <PageLayout>
        <div className="w-full px-4 py-16 md:px-8">
          <EmptyState
            icon="ri-error-warning-line"
            title="分类不存在"
            description="你访问的电影分类不存在或已下线，去首页看看吧。"
            actionText="返回首页"
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8">
        {/* Breadcrumb */}
        <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
          <Link to="/category/action" className="cursor-pointer hover:text-primary-600">电影分类</Link>
          <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        {/* Category header */}
        <div className="mb-8 flex items-start gap-4 rounded-lg bg-primary-900 p-6 md:p-8">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-white md:h-14 md:w-14">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black text-white md:text-3xl">
              {category.name}电影
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300">{category.desc}</p>
            <p className="mt-2 text-xs text-background-400">共收录 {source.length} 部影片</p>
          </div>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row">
          <FilterPanel
            regionOptions={regions}
            yearOptions={years}
            regionsSelected={regionsSelected}
            yearsSelected={yearsSelected}
            sort={sort}
            onToggleRegion={(k) => toggle(regionsSelected, setRegionsSelected, k)}
            onToggleYear={(k) => toggle(yearsSelected, setYearsSelected, k)}
            onSortChange={(k) => {
              setSort(k);
              setPage(1);
            }}
            onReset={reset}
            activeCount={activeCount}
          />

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex items-center justify-between">
              <p className="text-sm text-foreground-500">
                共找到 <span className="font-bold text-foreground-900">{filtered.length}</span> 部影片
              </p>
            </div>

            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-search-eye-line"
                title="未找到符合条件的电影，请调整筛选"
                description="试着放宽地区或年份的条件，或直接重置筛选条件。"
                actionText="重置筛选"
                onAction={reset}
              />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 md:gap-4">
                  {pageItems.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}